/* eslint-disable @typescript-eslint/naming-convention */
import { useContext, useRef, useState, useCallback } from "react";
// contexts
import { ApiContext, CART_TYPE } from "@swiggy-private/checkout-core";
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartActionTypes } from "@cart-client/reducers/CartReducer";
// helpers
import { useMountedRef, captureError, useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { generateNativeLocalGiftingCartFromStates, isGiftingCartEmpty } from "@cart-client/helpers/GiftingCart";
import { updateGiftingCart } from "@cart-client/apis/GiftingCart";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";
// interfaces
import { ApiResponse } from "@cart-client/interfaces/Api";
import { NativeLocalCart, ApiMenuCartItem, GiftingCartApiResponse, GiftingLocalCart } from "@swiggy-private/cart-types";
// constants
import { HTTP_STATUS_CODES } from "@cart-client/constants/index";
import { logCartViewedEvent } from "@cart-client/helpers/GiftingClevertap";

interface UpdateCartState {
    updateCartInProgress: boolean;
    updateCartData?: GiftingLocalCart | null;
    updateCartError?: { httpStatusCode?: number };
}

type UseUpdateCartReturnType = [
    UpdateCartState,
    (
        data: NonNullable<UpdateCartState["updateCartData"]>,
    ) => Promise<ApiResponse<GiftingCartApiResponse> | null | undefined>,
    VoidFunction,
];

const getUpdateCartParams = (updateCartData: GiftingLocalCart): GiftingLocalCart => {
    const newCartItems = updateCartData.foodCartItems?.map((cartItem: ApiMenuCartItem) => {
        const { valid_addons, valid_variants, valid_variants_v2, ...rest } = cartItem;
        if (!isEmpty(rest?.customer_request_messages)) {
            rest.customer_request_messages = rest?.customer_request_messages?.slice(0, rest.quantity) || [];
        }
        return rest;
    });

    updateCartData.foodCartItems = newCartItems as ApiMenuCartItem[];
    return { ...updateCartData, cart_type: CART_TYPE.GIFTING };
};

export const usePerformGiftingCartUpdate = (): UseUpdateCartReturnType => {
    const isMountedRef = useMountedRef();
    const apiContextValue = useContext(ApiContext);
    const { setCartState, cartState } = useContext(CartContext);
    const apiCallRef = useRef(false);
    const [updateCartState, setUpdateCartState] = useState<UpdateCartState>({ updateCartInProgress: false });
    const apiHost = apiContextValue.host.unwrap();

    const handleUpdateCartSuccess = useCallback(
        async (response: ApiResponse<GiftingCartApiResponse>): Promise<void> => {
            if (response) {
                if (isGiftingCartEmpty(response.data)) {
                    setCartState({
                        type: CartActionTypes.CLEAR_LOCAL_CART,
                        payload: {
                            reviewedCart: response,
                        },
                    });
                } else {
                    // log clevertap event for cart viewed
                    logCartViewedEvent(response);
                    setCartState({
                        type: CartActionTypes.UPDATE_CART,
                        payload: {
                            uniqueIdForCart: randomStringIdGenerator(),
                            localCart:
                                (response.data &&
                                    generateNativeLocalGiftingCartFromStates(response.data, cartState?.localCart)) ||
                                null,
                            reviewedCart: response,
                        },
                    });
                }
            }
        },
        [setCartState, cartState],
    );

    const performCartUpdate = useCallback(
        async (updateCartData: GiftingLocalCart): Promise<ApiResponse<GiftingCartApiResponse> | null> => {
            if (!isMountedRef.current || apiCallRef.current || !updateCartData) {
                return null;
            }
            apiCallRef.current = true;
            const updateCartParams = getUpdateCartParams(updateCartData);
            try {
                const updateCartResponse = await updateGiftingCart(apiHost, updateCartParams);
                await handleUpdateCartSuccess(updateCartResponse);
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setUpdateCartState({ updateCartInProgress: false });
                }
                return updateCartResponse;
            } catch (e) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const httpStatusCode = e?.status;
                let updateCartError;
                if (httpStatusCode == HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
                    updateCartError = {
                        httpStatusCode,
                    };
                }
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setUpdateCartState({ updateCartData: null, updateCartInProgress: false, updateCartError });
                }
                captureError(e);
                return null;
            }
        },
        [apiHost, handleUpdateCartSuccess, isMountedRef],
    );

    const doUpdateCart = useCallback(
        async (updateCartData: GiftingLocalCart): Promise<ApiResponse<GiftingCartApiResponse> | null | undefined> => {
            if (isMountedRef.current && !updateCartState.updateCartInProgress) {
                // food cart updates happen on cartItems, we mutate the cartItems to foodCartItems for gifting cart updates
                if (!isEmpty(updateCartData?.cartItems)) {
                    updateCartData.foodCartItems = [...updateCartData.cartItems];
                    delete updateCartData.cartItems;
                }
                setUpdateCartState({ updateCartInProgress: true, updateCartData });
                return await performCartUpdate(updateCartData);
            }
        },
        [isMountedRef, updateCartState.updateCartInProgress, performCartUpdate],
    );

    const reloadCart = useCallback(async () => {
        if (isMountedRef.current && !updateCartState.updateCartInProgress) {
            setUpdateCartState({
                updateCartInProgress: true,
                updateCartData: { ...cartState.localCart },
            });
            await performCartUpdate({
                ...(cartState.localCart as NativeLocalCart),
            });
        }
    }, [isMountedRef, updateCartState.updateCartInProgress, cartState.localCart, performCartUpdate]);

    useEffectOnce(() => {
        // is mounted check..
        if (
            !isMountedRef.current ||
            apiCallRef.current ||
            !updateCartState.updateCartInProgress ||
            !updateCartState.updateCartData
        ) {
            return;
        }

        apiCallRef.current = true;
        const updateCartData = updateCartState.updateCartData;
        const updateCartParams = getUpdateCartParams(updateCartData);

        updateGiftingCart(apiHost, updateCartParams)
            .then(handleUpdateCartSuccess)
            .catch(captureError)
            .then(() => {
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setUpdateCartState({ updateCartData, updateCartInProgress: false });
                }
            });
    });

    return [updateCartState, doUpdateCart, reloadCart];
};
